import { Button, Grid } from "@mui/material"
import { hasRole } from "auth/hasRole"
import { IApiFunc, IApiResult } from "auth/interface/api.interface"
import { useApi } from "auth/useApi3"
import { AppContext } from "components/AppContextProvider"
import { ClpTableEnum } from "components/ClpTable/ClpTableEnum"
import { StyledTableButtonGroup } from "components/ClpTable/components/actions/TableButton/tableButtonGroup.styled"
import { TableButtonTypeEnum } from "components/ClpTable/components/actions/TableButton/tableButtonProps.interface"
import { apiEndpoints } from "generated/apiEndpoints"
import { allRoles } from "generated/roles"
import { useContext, useEffect, useMemo, useState } from "react"
import { VehicleExpense } from "../vehicle.interfaces"
import { ExpenseSidebar } from "./ExpenseSidebar"
import { ActionType, ModalState } from "./InspectionBookingSidebar.interfaces"
import { initialModalState, ModalDialog } from "./ModalDialog"
import { useTranslation } from "react-i18next"
import { VehicleExpensesProps } from "./VehicleExpenses.interfaces"
import ClpTable from "components/ClpTable"
import TableButton from "components/ClpTable/components/actions/TableButton/TableButton"
import moment from "moment/moment"
import PaperBox from "components/PaperBox"

function numberToLocaleCurrencyFormat(amount: number): string {
  const languageTag =
    navigator.languages.find((language) => language.match(/sv|sv\-SE/g)) || navigator.language
  return amount.toLocaleString(languageTag, { minimumFractionDigits: 2 })
}

const sidebarId = "VehicleExpenseForm"

export const VehicleExpenses = ({ 
  vehicleData, 
  fetchExpenses, 
  expensesResult,
  tabValueIndex,
  tabValue,
  setTabHistory

} :  VehicleExpensesProps) => {
  
  const { t } = useTranslation()
  const appContext = useContext(AppContext)
  const [expensesDeleteResult, expensesDeleteApi] = useApi() as [IApiResult, IApiFunc]
  const [clickedExpense, setClickedExpense] = useState<VehicleExpense>()
  const [modalData, setModalData] = useState<ModalState>(initialModalState)

  useEffect(() => {
    if (appContext.isSidebarOpen) return
    setClickedExpense(undefined)
  }, [appContext.isSidebarOpen])
 
  useEffect(() => {
    if (expensesDeleteResult.status !== 2) return
      fetchExpenses(vehicleData.id.toString())
  }, [expensesDeleteResult])

  useEffect(() => {
    setTabHistory((prev: any) => {
      const alreadyVisited = prev.includes(tabValueIndex.VehicleExpenses)
      if (!alreadyVisited && tabValue === tabValueIndex.VehicleExpenses) {
        fetchExpenses(vehicleData.id.toString())
      }
    
      if (!alreadyVisited) {
        return [...prev, tabValueIndex.VehicleExpenses]
      }
      return prev

    })
  }, [tabValue])

  const columns: any = useMemo(
    () => [
      {
        accessorKey: "name",
        header: t("vehicleregistry.cost", "Cost"),
      },
      {
        accessorKey: "description",
        header: t("global.description", "Description"),
      },
      {
        accessorKey: "date",
        accessorFn: (row: VehicleExpense) =>
          row?.date ? moment(row?.date).local().format("YYYY-MM-DD") : "",
        header: t("global.date", "Date"),
      },
      {
        accessorKey: "amount",
        accessorFn: (row: VehicleExpense) => (row?.amount ? numberToLocaleCurrencyFormat(row?.amount) : ""),
        header: t("vehicleregistry.amount_sek", "Amount (SEK)"),
        align: "right"
      }
    ],
    []
  )

  useEffect(() => {
    if (hasRole(appContext.appUser, allRoles.VehicleRegistryAdmin)) {
      columns.push({
        cell: ({ cell }: any) => {
          return (
            <StyledTableButtonGroup>
              <TableButton
                type={TableButtonTypeEnum.Edit}
                handleOnClick={() => {
                  setClickedExpense(cell.row.original)
                  appContext.toggleSideBar(sidebarId, true)
                }}
              />
              <TableButton
                type={TableButtonTypeEnum.Delete}
                handleOnClick={() => {
                  const expenseName = cell.row.original.name
                  setClickedExpense(cell.row.original)
                  setModalData({
                    actionType: ActionType.Confirm,
                    isOpen: true,
                    title: t("vehicleregistry.delete_cost", "Delete cost"),
                    message: `${t("vehicleregistry.are_you_sure_you_want_to_delete", "Are you sure you want to delete?")} ${expenseName}?`
                  })
                }}
              />
            </StyledTableButtonGroup>
          )
        },
        accessorKey: "action",
        header: "",
        align: "right",
        hiddenFilter: true
      })
    }
  }, [])

  return (
    <>
      <ExpenseSidebar
        vehicleData={vehicleData}
        clickedExpense={clickedExpense}
        handleSubmit={() => {
          fetchExpenses(vehicleData.id.toString())
        }}
      />
      <ModalDialog
        modalData={modalData}
        setModalData={setModalData}
        setShowSidebar={appContext.toggleSideBar}
        handleSubmit={() => {
          const expenseId = clickedExpense?.id
          if (!expenseId) return
          expensesDeleteApi.del(
            apiEndpoints.vehicleexpense.deletevehicleexpense.replace("{id}", expenseId.toString())
          )
        }}
      />
      <PaperBox mt={0} sx={{ padding: "0px !important" }}>
        <Grid container justifyContent="flex-end" pt={{ xs: 2, sm: 3 }} pr={{ xs: 2, sm: 3 }}>
          <Button
            disabled={appContext.isSidebarOpen}
            variant="contained"
            onClick={() => {
              setClickedExpense(undefined)
              appContext.toggleSideBar(sidebarId, true)
            }}>
            {t("vehicleregistry.add_cost", "Add cost")}
          </Button>
        </Grid>
        <ClpTable
          columns={columns}
          rows={expensesResult.status === 2 && expensesResult.data}
          loadingState={expensesResult.status}
          tableId={ClpTableEnum.VehicleRegistryDetailsExpenses}
        />
      </PaperBox>
    </>
  )
}
